import gql from 'graphql-tag'

export const parklioBookingBayAccess = gql`
	query parklioBookingBayAccess ($bookingId: ID!) {
		parklioBookingBayAccess(bookingId: $bookingId) {
			bayLabel
			zone
			accessState
			reachability
			position
			carPresent
		}
	}
`

export const eventById = gql`
	query eventById ($id: ID!) {
		Event(id: $id) {
			id
			plate
			pcLoggedAt
		}
	}
`

export const getCameraStreams = gql`
    query StreamUrl($id: ID!) {
      StreamUrl(id: $id) {
        url
        name
        signId
        gateId
        gateType
        error
        terminalHidden
      }
    }
`

export const getParklioBays = gql`
    query ParklioBaysForInstallation($installationId: ID!) {
        parklioBaysForInstallation(installationId: $installationId) {
            id
            label
            status
        }
    }
`

export const parklioAvailableBays = gql`
    query ParklioAvailableBays($rateCardId: ID!, $startDay: String!, $endDay: String!) {
        parklioAvailableBays(rateCardId: $rateCardId, startDay: $startDay, endDay: $endDay) {
            id
            label
            zone
        }
    }
`

export const getGates = gql`
    query GatesForInstallation($id:ID!) {
          gatesForInstallation(installationId: $id) {
            id
            type
            gateDescription
        }
    }`


export const listEvents = gql`
    query listEvents($offset: Int!, $gateType: GateTypeEnum, $search: String) {
        listEvents(offset: $offset, gateType: $gateType, search: $search) {
            items {
                id
                plate
                gateDescription
                pcLoggedAt
            }
            count
        }
    }
`;

const walkupPinFields = `
                id
                status
                pin
                validFrom
                validTo
`

/* Only sent for facilities with walk-up PINs, so the field never reaches an
 * API that doesn't have it yet and every other hotel keeps the plain query. */
export const listEventsWithWalkupPins = gql`
    query listEventsWithWalkupPins($offset: Int!, $gateType: GateTypeEnum, $search: String) {
        listEvents(offset: $offset, gateType: $gateType, search: $search) {
            items {
                id
                plate
                gateDescription
                pcLoggedAt
                pinpadAccess {
${walkupPinFields}
                }
            }
            count
        }
    }
`;

export const walkupPinByEvent = gql`
    query walkupPinByEvent($id: ID!) {
        Event(id: $id) {
            id
            pinpadAccess {
${walkupPinFields}
            }
        }
    }
`;

/* Every field the booking forms read off a guest card, shared so a card
 * fetched by id arrives in the same shape as one off the active list. */
const rateCardFields = `
			id
			lineageId
			version

			name
			type
			calculationMode
			serviceFeePercent
			isValet

			entryAfterTime
			exitBeforeTime
			bookingRateAudPerDay
			weekendSurchargeRate
			overstayRateAudPerHour
			overstayFlagfallAud
			overstayGraceMinutes

			bookingRateAudPerHour
			chargeOnExpiryAud

			hoursUntilExpiry
			maximum24HourAud

			bookingRateAudPerMonth

			guestName
			guestEmail
			guestCompany
			guestDepartment
			guestPhone
			guestRoom
			guestApartmentNumber
			guestReservation
			guestEmployeeCode
			guestPosition
			paymentStripe
			parklioBayAssignment
			valetPayLater
			valetRequestFeeAud
			includeCarType,
			includeCarNotes,
			isVip
			defaultVipContactId

			isArchived
			createdAt

			allowFutureDate

			activeDaysOfWeek
			activeFromMinute
			activeToMinute
			fixedSessionPriceAud
			fixedSessionDurationMinutes
			fixedSessionFullDayFeeAud

			fixedBookingStartDate
			fixedBookingEndDate
			packagePriceAud

			lot {
				id
				name
				type
			}
`

export const allActiveRateCards = gql`
	query allActiveRateCards ($installationId: ID!) {
		allRateCards(
			filter: {
				installationId: $installationId
				isArchived: false
			}
			orderBy: name_ASC
		) {
${rateCardFields}
		}
	}
`

/* An archived card is not in the active list, but a booking sold on one still
 * has to be readable and editable. */
export const rateCardById = gql`
	query rateCardById ($rateCardId: ID!) {
		rateCard (id: $rateCardId) {
${rateCardFields}
		}
	}
`

export const listBookings = gql`
    query listBookings(
        $offset: Int!
        $limit: Int
        $search: String
        $status: String
        $parklioEnabled: Boolean = false
    ) {
        listBookings(
            offset: $offset
            limit: $limit
            search: $search
            status: $status
        ) {
            items {
                id
                plate
                guestName
                startDay
                endDay
                status
                parklioBayLabel @include(if: $parklioEnabled)

                rateCard {
                    name
                    version
                    type
                    parklioBayAssignment
                }

                bookingExtras {
                    id
                }
            }
            count
        }
    }
`;


export const listBookingsWithExtras = gql`
	query Extras($installationId: ID!) {
		bookingsWithExtras(installationId: $installationId) {
			id
			status
			plate
			startDay
			endDay
			guestName
			rateCard {
				name
				version
				type
			}
			bookingExtras {
				rateCardExtra {
					amount
					description
				}
			}
		}
	}
`

export const listGateCharges = gql`
    query QueryInstallationCharges($input: QueryChargeByInstallationInput!) {
      getGateChargesForInstallation(input: $input) {
        id
        createdAt
        plate
        approved
        amount
        interval
        gateName
      }
}
`

export const bookingById = gql`
	query bookingById ($bookingId: ID!, $includeParkingSessions: Boolean!, $includeVipAlertLogs: Boolean! = false) {
		booking (id: $bookingId) {
			id
			status
			installationId
			plate
			staffName
			reason
			guestName
			guestEmail
			guestCompany
			guestDepartment
			guestPhone
			guestRoom
			guestApartmentNumber
			guestReservation
			guestEmployeeCode
			guestPosition
			stripeSource
			stripeCustomerId
			carType
			carNotes
			isVip
			vipContacts {
				contactId
				alertedAt
			}

			startDay
			endDay

			createdAt
			currentAt
			closedAt
			finalisedAt

			pmsInformation

			pinpadAccess {
				id
				status
				pin
				validFrom
				validTo
				lastError
				lastSyncedAt
			}

			rateCard {
				id
				lineageId
				version

				name
				lot {
					name
				}
				type
				calculationMode
				serviceFeePercent
				publicUrl

				entryAfterTime
				exitBeforeTime
				bookingRateAudPerDay
				bookingRateAudPerMonth
				overstayRateAudPerHour
				weekendSurchargeRate
				overstayFlagfallAud
				overstayGraceMinutes

				bookingRateAudPerHour
				chargeOnExpiryAud

				hoursUntilExpiry
				maximum24HourAud

				guestName
				guestEmail
				guestCompany
				guestDepartment
				guestPhone
				guestRoom
				guestApartmentNumber
				guestReservation
				guestEmployeeCode
				guestPosition
				paymentStripe
				valetPayLater
				valetRequestFeeAud
				includeCarType
				includeCarNotes
				isVip
				isValet
				defaultVipContactId

				isArchived
				createdAt

				allowFutureDate

				fixedBookingStartDate
				fixedBookingEndDate
				packagePriceAud
			}

			charges (orderBy: createdAt_DESC) {
				amountAud
				error
				createdAt
				finalisedAt
			}

			alertLogs @include(if: $includeVipAlertLogs) {
				id
				bookingId
				alertType
				providerType
				status
				errorCode
				errorMessage
				sentAt
				deliveredAt
				failedAt
				createdAt
				updatedAt
				contact {
					id
					name
				}
			}

			emails {
				id
				createdAt
				templateClass
				mailgunId
				finalisedAt
			}

			events {
				gateType
        		gateDescription
				pcLoggedAt
			}

			parkingSessions @include(if: $includeParkingSessions) {
				id
				createdAt
				updatedAt
				facilityId
				plate
				normalizedPlate
				entryObservationId
				entryGateId
				entryAt
				exitObservationId
				exitGateId
				exitAt
				status
				parkedMinutes
				bookingId
				entryGateDescription
				exitGateDescription
			}

			bookingExtras {
				rateCardExtra {
					id
					description
					type
					amount
				}
			}

			refunds (orderBy: createdAt_ASC) {
				id
				status
				amountAud
				createdAt
				createdBy {
					name
				}
				updatedAt
			}

			cancellationRequests {
				id
				isProcessed
			}
		}
	}
`

export const getStripePaymentMethods = gql`
	query GetStripePaymentMethods($customerId: String!, $installationId: ID) {
		getStripePaymentMethods(customerId: $customerId, installationId: $installationId) {
			card {
				brand
				last4
				exp_month
				exp_year
			}
		}
	}
`
